import { AppIcon } from '@/components/ui/app-icon';
import { AdminAgentsPlaysPanel } from '@/components/admin-agents-plays-panel.component';
import { AdminAppsumoSectionPanel } from '@/components/admin-appsumo-section-panel.component';
import {
  DEFAULT_APPSUMO_VIEW,
  isAppsumoView,
  LEGACY_APPSUMO_TABS,
  type AppsumoView,
} from '@/components/admin-appsumo.util';
import { AdminChurnRiskPanel } from '@/components/admin-churn-risk-panel.component';
import { AdminCluChatsPanel } from '@/components/admin-clu-chats-panel.component';
import { AdminCreditUsagePanel } from '@/components/admin-credit-usage-panel.component';
import { AdminCrmOverviewPanel } from '@/components/admin-crm-overview-panel.component';
import { AdminFraudDetectionPanel } from '@/components/admin-fraud-detection-panel.component';
import { AdminIpBlocklistPanel } from '@/components/admin-ip-blocklist-panel.component';
import { AdminLiveUsersPanel } from '@/components/admin-live-users-panel.component';
import { AdminManagementNav } from '@/components/admin-management-nav.component';
import { AdminOptOutsPanel } from '@/components/admin-opt-outs-panel.component';
import { AdminOrgHub } from '@/components/admin-org-hub.component';
import {
  AdminOverviewPanel,
  type OverviewTarget,
} from '@/components/admin-overview-panel.component';
import { AdminProviderHitRatesPanel } from '@/components/admin-provider-hit-rates-panel.component';
import { AdminRewardCreditsPanel } from '@/components/admin-reward-credits-panel.component';
import { AdminSignupsPanel } from '@/components/admin-signups-panel.component';
import { AdminTrialsPanel } from '@/components/admin-trials-panel.component';
import {
  DEFAULT_REWARD_SOURCE,
  isRewardSource,
  LEGACY_REWARD_TABS,
  type RewardSource,
} from '@/components/admin-reward-credits.util';
import {
  AdminTrialDomainAllowlistPanel,
  TRIAL_DOMAIN_ALLOWLIST_EXPLANATION,
} from '@/components/admin-trial-domain-allowlist-panel.component';
import { AppLayout } from '@/components/app-layout';
import { AuthCheck } from '@/components/auth-check.component';
import { Head } from '@/components/head.component';
import { Surface } from '@/components/ui/surface';
import { useAdminPendingRewards } from '@/hooks/use-admin-pending-rewards.hook';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { CardTrialsPanel } from '@/containers/management/card-trials/card-trials-panel.component';
import {
  type FinanceView,
  LEGACY_MANAGEMENT_TABS,
} from '@/containers/finance/finance-view';
import { useGetFinanceAccessMeQuery } from '@/store/features/admin-finance.feature';
import { useUser } from '@clerk/react';
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  iconArrowLeft,
  iconBan,
  iconCoins,
  iconCreditCard,
  iconShieldExclamation,
  iconChartBar,
  iconGauge,
  iconGlobe,
  iconCalendarClock,
  iconMessage,
  iconMoon,
  iconPlug,
  iconRobot,
  iconStar,
  iconTicket,
  iconUserGear,
  iconUserPlus,
  iconUserSlash,
  iconWavePulse,
} from '@/lib/icons';

type IconShim = React.ComponentType<{ className?: string }>;
const Orgs: IconShim = (p) => <AppIcon icon={iconUserGear} {...p} />;
const Coins: IconShim = (p) => <AppIcon icon={iconCoins} {...p} />;
const Ticket: IconShim = (p) => <AppIcon icon={iconTicket} {...p} />;
const Star: IconShim = (p) => <AppIcon icon={iconStar} {...p} />;
const ShieldAlert: IconShim = (p) => (
  <AppIcon icon={iconShieldExclamation} {...p} />
);

const Globe: IconShim = (p) => <AppIcon icon={iconGlobe} {...p} />;
const Chats: IconShim = (p) => <AppIcon icon={iconMessage} {...p} />;
const Live: IconShim = (p) => <AppIcon icon={iconWavePulse} {...p} />;
const Ban: IconShim = (p) => <AppIcon icon={iconBan} {...p} />;
const Moon: IconShim = (p) => <AppIcon icon={iconMoon} {...p} />;
const Plug: IconShim = (p) => <AppIcon icon={iconPlug} {...p} />;
const HitRates: IconShim = (p) => <AppIcon icon={iconChartBar} {...p} />;
const Gauge: IconShim = (p) => <AppIcon icon={iconGauge} {...p} />;
const Robot: IconShim = (p) => <AppIcon icon={iconRobot} {...p} />;
const UserPlus: IconShim = (p) => <AppIcon icon={iconUserPlus} {...p} />;
const TrialClock: IconShim = (p) => <AppIcon icon={iconCalendarClock} {...p} />;
const OptOut: IconShim = (p) => <AppIcon icon={iconUserSlash} {...p} />;
const Card: IconShim = (p) => <AppIcon icon={iconCreditCard} {...p} />;

const STAFF_DOMAIN = 'cleanlist.ai';

// Nav order. Sections render grouped, in this order within each group.
const groups = [
  { id: 'overview', label: null },
  { id: 'orgs', label: 'Orgs & users' },
  { id: 'billing', label: 'Credits & billing' },
  { id: 'health', label: 'Product health' },
  { id: 'security', label: 'Security' },
] as const;

const sections = [
  {
    id: 'overview',
    group: 'overview',
    label: 'Overview',
    icon: Gauge,
    description:
      'The headline number from every section. Each tile refreshes on its own; click one to open its section.',
  },
  {
    id: 'organizations',
    group: 'orgs',
    label: 'Organizations',
    icon: Orgs,
    description:
      "Look up an org by name, id, or owner email. See its members, usage (enrichments, API, credits), and act on it — assign credits, change tier, or suspend the org's access.",
  },
  {
    id: 'signups',
    group: 'orgs',
    label: 'Signups',
    icon: UserPlus,
    description:
      'New users and orgs per UTC day, counted from our database: deleted accounts and @cleanlist.ai staff are left out unless you include them. Orgs split by how they arrived (free, trial, AppSumo); fraud-denied = signups the fraud check denied credits.',
  },
  {
    id: 'live-users',
    group: 'orgs',
    label: 'Live users',
    icon: Live,
    description:
      'Users with the app open right now, by org. Refreshes every 10s; a closed tab drops off within ~75s.',
  },
  {
    id: 'churn-risk',
    group: 'orgs',
    label: 'Churn risk',
    icon: Moon,
    description:
      'Orgs with no credit spend or Clu message in the selected window, paying plans first, then the longest inactive. Click an org to open its hub.',
  },
  {
    id: 'trials',
    group: 'billing',
    label: 'Trials',
    icon: TrialClock,
    description:
      'Free trials started in the selected window, plus every trial still running, with how each one ended. Conversion counts trials that have ended: converted ÷ (converted + expired + cancelled). Click an org to open its hub.',
  },
  {
    id: 'card-trials',
    group: 'billing',
    label: 'Card trials',
    icon: Card,
    description:
      'Live card-trial funnel since go-live (Sep 30, 9:42 AM ET): signups → trial page → card on file, every trial’s 14-day runway, and how likely each one is to pay at renewal. Stripe and PostHog, refreshed every 15 minutes; days are Toronto days.',
  },
  {
    id: 'credit-usage',
    group: 'billing',
    label: 'Credit usage',
    icon: Coins,
    description:
      'Credit consumption (spend − refunds) over the last 24h, 7d or 30d, the live view of the daily Slack digest. Look up any org for its members and tools, or open one from the top list.',
  },
  {
    id: 'reward-credits',
    group: 'billing',
    label: 'Reward credits',
    icon: Star,
    description:
      'Every staff-approved credit reward in one place: AppSumo reviews, Product Hunt upvotes, onboarding calls and G2 reviews. Approving grants the reward atomically with the credit ledger.',
  },
  {
    id: 'appsumo',
    group: 'billing',
    label: 'AppSumo',
    icon: Ticket,
    description:
      'AppSumo activity by day, license lookup and attribution, and refund abusers.',
  },
  {
    id: 'agents-plays',
    group: 'health',
    label: 'Agents & plays',
    icon: Robot,
    description:
      "Play and agent runs across every org: what is running now (refreshes every 15s), outcomes over the window, each play's pass rate with the most failing first, and the latest failures with the step that failed. Pass rate = succeeded ÷ (succeeded + failed); dry runs and proposal test plays are left out.",
  },
  {
    id: 'provider-hit-rates',
    group: 'health',
    label: 'Provider hit rates',
    icon: HitRates,
    description:
      'Results found ÷ attempts for each enrichment provider in the waterfall, by lookup type, over the last 24h, 7d or 30d.',
  },
  {
    id: 'crm-overview',
    group: 'health',
    label: 'CRM overview',
    icon: Plug,
    description:
      'Every org CRM connection and its health: disconnected = tokens cleared by a failed OAuth refresh, erroring = an operation failed in the window. Expand a row for its operations and recent errors; click an org to open its hub.',
  },
  {
    id: 'clu-chats',
    group: 'health',
    label: 'Clu chats',
    icon: Chats,
    description:
      'Clu conversations across every org. Open one to read it; the URL links straight to that chat.',
  },
  {
    id: 'fraud',
    group: 'security',
    label: 'Fraud',
    icon: ShieldAlert,
    description:
      'How signups score at the fraud check: claims vs denials, the risk split, the rules that fire most, and the latest denied signups.',
  },
  {
    id: 'blocked-ips',
    group: 'security',
    label: 'Blocked IPs',
    icon: Ban,
    description:
      'IPs permanently blocked for probing exploit paths, with ipwhois location and network. Remove an entry to restore access.',
  },
  {
    id: 'trial-domains',
    group: 'security',
    label: 'Trial domains',
    icon: Globe,
    description: TRIAL_DOMAIN_ALLOWLIST_EXPLANATION,
  },
  {
    id: 'opt-outs',
    group: 'security',
    label: 'Opt-outs',
    icon: OptOut,
    description:
      'People who asked us to stop enriching them. Confirm the request from the inbox, check their email and LinkedIn profile, then block them: no customer can enrich them after that. Someone who asks to be enriched again can be removed with a reason, and the removal is kept. The list keeps hashes only, so it never shows who is on it.',
  },
] as const;

type SectionId = (typeof sections)[number]['id'];

const SECTION_IDS = sections.map((s) => s.id) as readonly SectionId[];
const DEFAULT_SECTION: SectionId = 'overview';

const isSectionId = (value: unknown): value is SectionId =>
  typeof value === 'string' && SECTION_IDS.includes(value as SectionId);

const ManagementPage = () => {
  const { user, isSignedIn } = useUser();
  const navigate = useNavigate();
  const email = user?.primaryEmailAddress?.emailAddress ?? '';
  const isStaff = email.toLowerCase().endsWith(`@${STAFF_DOMAIN}`);

  // Active tab lives in the URL (?tab=) so it survives reloads, browser
  // back/forward, and is shareable. `replace` keeps tab switches out of the
  // history stack. `?org=` (optional) deep-links straight into an org's detail
  // view — Slack alerts (credit digest, AppSumo) link here for one-click
  // actioning. `?chat=` is the Clu chat open in the Clu chats tab, so staff
  // can share a link to one chat. `?q=` is the Churn risk search. `?view=` is
  // the AppSumo sub-tab.
  const {
    tab: activeTab,
    org: initialOrgId,
    chat: openChatId,
    source: rewardSource,
    q: churnQuery,
    view: appsumoView,
    finance: legacyFinance,
  } = useSearch({
    from: '/management',
  });
  const setActiveTab = useCallback(
    (id: SectionId) =>
      navigate({ to: '/management', search: { tab: id }, replace: true }),
    [navigate]
  );
  const setOpenChatId = useCallback(
    (chat: string | undefined) =>
      navigate({
        to: '/management',
        search: { tab: 'clu-chats', chat },
        replace: true,
      }),
    [navigate]
  );

  const setChurnQuery = useCallback(
    (q: string) =>
      navigate({
        to: '/management',
        search: { tab: 'churn-risk', q: q || undefined },
        replace: true,
      }),
    [navigate]
  );

  const setRewardSource = useCallback(
    (source: RewardSource) =>
      navigate({
        to: '/management',
        search: { tab: 'reward-credits', source },
        replace: true,
      }),
    [navigate]
  );

  const setAppsumoView = useCallback(
    (view: AppsumoView) =>
      navigate({
        to: '/management',
        search: { tab: 'appsumo', view },
        replace: true,
      }),
    [navigate]
  );

  // Lazy keep-alive: a panel mounts only once its tab is first visited, then
  // stays mounted (hidden) so its search inputs/results persist across tab
  // switches.
  const [visited, setVisited] = useState<Set<SectionId>>(
    () => new Set([activeTab])
  );
  useEffect(() => {
    setVisited((prev) =>
      prev.has(activeTab) ? prev : new Set(prev).add(activeTab)
    );
  }, [activeTab]);

  useEffect(() => {
    if (isSignedIn && !isStaff) {
      navigate({ to: '/' });
    }
  }, [isSignedIn, isStaff, navigate]);

  // Treasury and Revenue moved to /finance. An old link opens the same page
  // there for people on the finance list, and Overview for everyone else.
  const financeAccess = useGetFinanceAccessMeQuery(undefined, {
    skip: !isStaff || !legacyFinance,
  });
  const financeAllowed = financeAccess.data?.allowed;
  const financeFailed = financeAccess.isError;
  useEffect(() => {
    if (!legacyFinance) return;
    if (financeAllowed)
      navigate({
        to: '/finance',
        search: { view: legacyFinance },
        replace: true,
      });
    else if (financeAllowed === false || financeFailed)
      navigate({
        to: '/management',
        search: { tab: DEFAULT_SECTION },
        replace: true,
      });
  }, [legacyFinance, financeAllowed, financeFailed, navigate]);

  const pending = useAdminPendingRewards({ skip: !isStaff });
  const badges: Partial<Record<SectionId, string>> = {
    'reward-credits':
      pending.total > 0
        ? `${pending.total}${pending.capped ? '+' : ''}`
        : undefined,
  };
  const groupLabel = (id: string) =>
    groups.find((g) => g.id === id)?.label ?? null;

  const openFromOverview = (section: OverviewTarget, source?: RewardSource) =>
    section === 'reward-credits' && source
      ? setRewardSource(source)
      : setActiveTab(section);
  const openOrg = (org: string) =>
    navigate({
      to: '/management',
      search: { tab: 'organizations', org },
      replace: true,
    });

  if (!isSignedIn || !isStaff || legacyFinance) {
    return null;
  }

  return (
    <AppLayout>
      <div className='flex flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:gap-8'>
        {/* On wide screens the nav scrolls on its own: capped to the page
            card's visible height (--drawer-inset-top is where the card
            starts, set by AppLayout), so every section stays reachable on a
            long tab. The 4px side padding keeps focus rings from clipping. */}
        <aside className='w-full shrink-0 lg:sticky lg:top-6 lg:-mx-1 lg:max-h-[calc(100dvh-var(--drawer-inset-top,8px)-4rem)] lg:w-58 lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:px-1'>
          <Link
            to='/settings'
            className='mb-2 -ml-1 inline-flex h-8 items-center gap-2 rounded-lg px-2 text-sm text-muted-foreground outline-hidden transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring touch-only:hidden'
          >
            <AppIcon icon={iconArrowLeft} className='h-3.5 w-3.5' />
            Settings
          </Link>
          <div className='mb-3 px-1'>
            <h1 className='text-sm font-semibold text-foreground'>
              Management
            </h1>
            <p className='text-xs text-muted-foreground'>Staff tools</p>
          </div>
          <AdminManagementNav
            sections={sections}
            groups={groups}
            active={activeTab}
            badges={badges}
            onSelect={setActiveTab}
            onOpenOrg={openOrg}
          />
        </aside>

        {/* On a phone, a wide table scrolls here, not the whole page.
            `relative` keeps a table's sr-only labels (absolute) inside it. */}
        <div className='min-w-0 grow touch-only:relative touch-only:overflow-x-auto'>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as SectionId)}
            className='w-full'
          >
            {sections.map((section) => (
              <TabsContent
                key={section.id}
                value={section.id}
                forceMount={visited.has(section.id) ? true : undefined}
                className='mt-0 space-y-4 data-[state=inactive]:hidden'
              >
                <header className='px-1'>
                  {groupLabel(section.group) && (
                    <p className='text-xs text-muted-foreground'>
                      {groupLabel(section.group)}
                    </p>
                  )}
                  <h2 className='text-lg font-semibold text-foreground'>
                    {section.label}
                  </h2>
                  {section.description && (
                    <p className='mt-0.5 max-w-3xl text-sm text-muted-foreground touch-only:hidden'>
                      {section.description}
                    </p>
                  )}
                </header>

                {/* Dashboard sections bring their own cards. */}
                {section.id === 'overview' ? (
                  <AdminOverviewPanel
                    active={activeTab === 'overview'}
                    onOpen={openFromOverview}
                  />
                ) : section.id === 'credit-usage' ? (
                  <AdminCreditUsagePanel onOpenOrg={openOrg} />
                ) : section.id === 'fraud' ? (
                  <AdminFraudDetectionPanel
                    onOpenBlockedIps={() => setActiveTab('blocked-ips')}
                    onOpenOrg={openOrg}
                  />
                ) : (
                  <Surface className='p-5'>
                    {section.id === 'organizations' && (
                      <AdminOrgHub initialOrgId={initialOrgId} />
                    )}
                    {section.id === 'signups' && <AdminSignupsPanel />}
                    {section.id === 'live-users' && (
                      <AdminLiveUsersPanel
                        active={activeTab === 'live-users'}
                      />
                    )}
                    {section.id === 'churn-risk' && (
                      <AdminChurnRiskPanel
                        active={activeTab === 'churn-risk'}
                        q={churnQuery}
                        onQueryChange={setChurnQuery}
                      />
                    )}
                    {section.id === 'trials' && <AdminTrialsPanel />}
                    {section.id === 'card-trials' && (
                      <CardTrialsPanel active={activeTab === 'card-trials'} />
                    )}
                    {section.id === 'agents-plays' && (
                      <AdminAgentsPlaysPanel
                        active={activeTab === 'agents-plays'}
                      />
                    )}
                    {section.id === 'provider-hit-rates' && (
                      <AdminProviderHitRatesPanel
                        active={activeTab === 'provider-hit-rates'}
                      />
                    )}
                    {section.id === 'crm-overview' && <AdminCrmOverviewPanel />}
                    {section.id === 'clu-chats' && (
                      <AdminCluChatsPanel
                        showHeader={false}
                        active={activeTab === 'clu-chats'}
                        chatId={openChatId}
                        onChatChange={setOpenChatId}
                      />
                    )}
                    {section.id === 'appsumo' && (
                      <AdminAppsumoSectionPanel
                        view={appsumoView ?? DEFAULT_APPSUMO_VIEW}
                        onViewChange={setAppsumoView}
                      />
                    )}
                    {section.id === 'reward-credits' && (
                      <AdminRewardCreditsPanel
                        source={rewardSource ?? DEFAULT_REWARD_SOURCE}
                        onSourceChange={setRewardSource}
                      />
                    )}
                    {section.id === 'blocked-ips' && (
                      <AdminIpBlocklistPanel showHeader={false} />
                    )}
                    {section.id === 'trial-domains' && (
                      <AdminTrialDomainAllowlistPanel showHeader={false} />
                    )}
                    {section.id === 'opt-outs' && <AdminOptOutsPanel />}
                  </Surface>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </AppLayout>
  );
};

export const Route = createFileRoute('/management')({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    tab: SectionId;
    org?: string;
    chat?: string;
    source?: RewardSource;
    q?: string;
    view?: AppsumoView;
    finance?: FinanceView;
  } => {
    // The reward queues used to be separate tabs; old links pick the sub-tab.
    const legacySource =
      typeof search.tab === 'string' &&
      Object.prototype.hasOwnProperty.call(LEGACY_REWARD_TABS, search.tab)
        ? LEGACY_REWARD_TABS[search.tab]
        : undefined;
    // Same for AppSumo abuse, now a sub-tab of AppSumo.
    const legacyView =
      typeof search.tab === 'string' &&
      Object.prototype.hasOwnProperty.call(LEGACY_APPSUMO_TABS, search.tab)
        ? LEGACY_APPSUMO_TABS[search.tab]
        : undefined;
    // Treasury and Revenue were tabs here before Finance had its own page.
    const legacyFinance =
      typeof search.tab === 'string' &&
      Object.prototype.hasOwnProperty.call(LEGACY_MANAGEMENT_TABS, search.tab)
        ? LEGACY_MANAGEMENT_TABS[search.tab]
        : undefined;
    return {
      tab: legacySource
        ? 'reward-credits'
        : legacyView
          ? 'appsumo'
          : isSectionId(search.tab)
            ? search.tab
            : DEFAULT_SECTION,
      org:
        typeof search.org === 'string' && search.org ? search.org : undefined,
      chat:
        typeof search.chat === 'string' && search.chat
          ? search.chat
          : undefined,
      source:
        legacySource ??
        (isRewardSource(search.source) ? search.source : undefined),
      // The router JSON-parses values, so `?q=123` arrives as a number.
      q:
        typeof search.q === 'string' || typeof search.q === 'number'
          ? String(search.q) || undefined
          : undefined,
      view:
        legacyView ?? (isAppsumoView(search.view) ? search.view : undefined),
      finance: legacyFinance,
    };
  },
  component: () => (
    <AuthCheck>
      <Head heading='Management' />
      <ManagementPage />
    </AuthCheck>
  ),
});
